/**
 * Remote Execution — embedded protocol runner (2.14.0).
 *
 * A dependency-free Node program materialised onto the remote target and
 * executed there. It wraps the real remote child (the Jensen CLI) and frames
 * all output as JSONL protocol frames, so child stdout/stderr can never corrupt
 * the control channel. It is deliberately NOT an agent and NOT a scheduler: it
 * reports observations; the Bucephalus control plane remains authoritative.
 *
 * Kept as a line-joined source string (no template literal / escape ambiguity)
 * so it can be shipped inside a materialisation envelope without file-system
 * path resolution.
 */

export const REMOTE_RUNNER_SOURCE: string = [
	'"use strict";',
	'const { spawn } = require("node:child_process");',
	'const { createHash } = require("node:crypto");',
	'const fs = require("node:fs");',
	'const os = require("node:os");',
	'const path = require("node:path");',
	"",
	"function frame(type, payload) {",
	'  process.stdout.write(JSON.stringify({ type, payload }) + "\\n");',
	"}",
	"",
	"function readJson(file) {",
	'  return JSON.parse(fs.readFileSync(file, "utf8"));',
	"}",
	"",
	"function sha256(file) {",
	"  try {",
	'    return createHash("sha256").update(fs.readFileSync(file)).digest("hex");',
	"  } catch {",
	"    return null;",
	"  }",
	"}",
	"",
	"async function main() {",
	"  const envelope = readJson(process.argv[2]);",
	"  const location = {",
	"    host: os.hostname(),",
	'    user: (os.userInfo() || {}).username || process.env.USERNAME || "",',
	"    cwd: envelope.launch.cwd,",
	"    pid: process.pid,",
	'    platform: process.platform + "/" + os.arch(),',
	"  };",
	"",
	'  frame("REMOTE_STARTED", {',
	"    protocolVersion: envelope.protocolVersion,",
	"    executionId: envelope.executionId,",
	"    remoteTargetId: envelope.remoteTargetId,",
	"    launchId: envelope.launchId,",
	"    fencing: envelope.fencing,",
	"    location: location,",
	"    command: { argv: [envelope.launch.command].concat(envelope.launch.args || []), cwd: envelope.launch.cwd },",
	"    startedAtMs: Date.now(),",
	"  });",
	"",
	"  const evidenceFiles = envelope.evidenceFiles || [];",
	"  const before = {};",
	"  for (const f of evidenceFiles) before[f] = sha256(path.resolve(envelope.launch.cwd, f));",
	"",
	"  const start = Date.now();",
	"  let child;",
	"  try {",
	"    child = spawn(envelope.launch.command, envelope.launch.args || [], {",
	"      cwd: envelope.launch.cwd,",
	"      shell: false,",
	'      stdio: ["ignore", "pipe", "pipe"],',
	"      env: Object.assign({}, process.env, envelope.launch.env || {}),",
	"    });",
	"  } catch (err) {",
	'    frame("REMOTE_ERROR", { code: "LAUNCH_FAILED", message: String((err && err.message) || err) });',
	'    frame("REMOTE_EXIT", { executionId: envelope.executionId, exitCode: 1, signal: undefined, cancelled: false });',
	"    process.exit(0);",
	"    return;",
	"  }",
	"",
	"  let settled = false;",
	"  const settle = () => { if (!settled) { settled = true; clearInterval(hb); } };",
	"  const hb = setInterval(() => {",
	'    frame("REMOTE_HEARTBEAT", { atMs: Date.now(), elapsedMs: Date.now() - start, alive: true });',
	"  }, envelope.heartbeatMs || 3000);",
	"",
	'  child.stdout.on("data", (d) => frame("REMOTE_STDOUT", { chunk: d.toString() }));',
	'  child.stderr.on("data", (d) => frame("REMOTE_STDERR", { chunk: d.toString() }));',
	'  child.on("error", (err) => frame("REMOTE_ERROR", { code: "EXEC_FAILED", message: String((err && err.message) || err) }));',
	"",
	"  // If the control channel closes (controller SSH died), the next stdout",
	"  // write errors with EPIPE; kill the child so it cannot orphan. stdin is",
	"  // at EOF after materialisation and is never a control channel, so it must",
	"  // not be used to kill the child.",
	'  process.stdout.on("error", () => { try { child.kill(); } catch {} });',
	"",
	'  child.on("close", (code, signal) => {',
	"    settle();",
	"    const after = {};",
	"    for (const f of evidenceFiles) after[f] = sha256(path.resolve(envelope.launch.cwd, f));",
	"    const items = [];",
	"    for (const f of evidenceFiles) {",
	'      items.push({ kind: "file_hash", path: f, sha256Before: before[f], sha256After: after[f] });',
	"    }",
	'    frame("REMOTE_EVIDENCE", {',
	"      executionId: envelope.executionId,",
	"      host: location.host,",
	"      user: location.user,",
	"      cwd: location.cwd,",
	"      items: items,",
	"    });",
	'    frame("REMOTE_RESULT", {',
	"      executionId: envelope.executionId,",
	"      success: code === 0,",
	'      summary: "remote child exit " + String(code),',
	"    });",
	'    frame("REMOTE_EXIT", {',
	"      executionId: envelope.executionId,",
	"      exitCode: code,",
	"      signal: signal || undefined,",
	"      cancelled: false,",
	"    });",
	"    process.exit(0);",
	"  });",
	"}",
	"",
	"main().catch((err) => {",
	'  frame("REMOTE_ERROR", { code: "RUNNER_ERROR", message: String((err && err.message) || err) });',
	'  frame("REMOTE_EXIT", { executionId: "(unknown)", exitCode: 1, signal: undefined, cancelled: false });',
	"  process.exit(0);",
	"});",
].join("\n");
